import { useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import s from "./Projects.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useHoldPoints, useProjectAttention, useProjects, useProjectVocabulary } from "../../api/queries";
import {
  ATTENTION_KINDS,
  ATTENTION_LABEL,
  ATTENTION_MEANING,
  formatMoney,
  PROJECT_STATUS_LABEL,
  PROJECT_STATUSES,
  SCHEDULE_LABEL,
  sortProjects,
  type AttentionKind,
  type AttentionRow,
  type ProjectRow,
  type ProjectStatus,
} from "../../domain/projects";
import { Button, Card, EmptyState, ErrorState, numClass, Select, Skeleton, Table } from "../../design/components";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { NewProjectDialog } from "./ProjectForms";
import { formatDate } from "../../lib/format";
import { saveFile } from "../../lib/saveFile";
import { projectsCsv } from "../../domain/projectsCsv";
import { t, tn } from "../../i18n";

/**
 * The capital programme, opened on the question anybody opens it with: what
 * is stopping work, and what is nobody looking at.
 *
 * The nine attention lists are rendered as nine tiles and never added
 * together. That is the platform's own instruction and it is not decoration:
 * "no_baseline" is not an on-time project and "never_inspected" is not a
 * project with nothing wrong, so a single "12 issues" figure would be twelve
 * different problems wearing one label and would send somebody to fix the
 * wrong one.
 */
export function ProjectsPage() {
  const { site, principal } = useSession();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const canWrite = hasPermission(principal, PERMISSION.projectsWrite);
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as ProjectStatus | null) || null;
  const category = params.get("category");
  const liveOnly = params.get("live") === "1";
  const focus = (params.get("focus") as AttentionKind | null) || null;

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const projects = useProjects({
    status: status ?? undefined,
    category: category ?? undefined,
    liveOnly: liveOnly || undefined,
  });
  const holds = useHoldPoints();
  const attention = useProjectAttention();
  const vocabulary = useProjectVocabulary();

  const rows = useMemo(() => sortProjects(projects.data ?? []), [projects.data]);

  return (
    <Page
      eyebrow={t("{site} · Engineering", { site: site.code })}
      title="Capital projects"
      actions={
        <div className={s.filters}>
          <Select value={status ?? ""} onChange={(e) => set("status", e.target.value || null)} aria-label={t("Status")}>
            <option value="">{t("Any status")}</option>
            {PROJECT_STATUSES.map((st) => (
              <option key={st} value={st}>
                {t(PROJECT_STATUS_LABEL[st])}
              </option>
            ))}
          </Select>
          <Select
            value={category ?? ""}
            onChange={(e) => set("category", e.target.value || null)}
            aria-label={t("Category")}
          >
            <option value="">{t("Any category")}</option>
            {(vocabulary.data?.categories ?? []).map((c) => (
              <option key={c.key} value={c.key}>
                {c.name}
              </option>
            ))}
          </Select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5 }}>
            <input type="checkbox" checked={liveOnly} onChange={(e) => set("live", e.target.checked ? "1" : null)} />
            {t("Live only")}
          </label>
          <Button
            onClick={() => {
              const rows = projects.data ?? [];
              if (rows.length === 0) return;
              saveFile(
                `capital-projects-${site.code}-${new Date().toISOString().slice(0, 10)}.csv`,
                "\uFEFF" + projectsCsv(rows),
                "text/csv;charset=utf-8",
              );
            }}
            disabled={(projects.data?.length ?? 0) === 0}
            title={t("Download these projects as a spreadsheet. Undeclared money is left blank, not zero.")}
          >
            {t("Export")}
          </Button>
          <Button
            variant="primary"
            onClick={() => setCreating(true)}
            disabled={!canWrite}
            title={canWrite ? undefined : t("Requires {permission}", { permission: PERMISSION.projectsWrite })}
          >
            {t("+ New project")}
          </Button>
        </div>
      }
    >
      {creating && (
        <NewProjectDialog onClose={() => setCreating(false)} onCreated={(id) => navigate(`/capital-projects/${id}`)} />
      )}
      {/* What is stopping work, said first and said plainly. */}
      {holds.data && holds.data.count > 0 && (
        <div className={s.stopBanner} data-testid="work-stopped">
          <div className={s.stopTitle}>
            {tn(
              holds.data.count,
              "{n} hold point unreleased — work is stopped",
              "{n} hold points unreleased — work is stopped",
            )}
          </div>
          <div className={s.note}>{holds.data.note}</div>
        </div>
      )}
      {holds.data && holds.data.count === 0 && (
        <div className={s.note} style={{ marginBottom: 12 }} data-testid="nothing-holding">
          {holds.data.note}
        </div>
      )}

      <Card
        title="Needs attention"
        sub={attention.data?.note ?? "Each list needs a different fix"}
      >
        {attention.isPending ? (
          <Skeleton rows={3} height={54} />
        ) : attention.isError ? (
          <ErrorState error={attention.error} retry={() => void attention.refetch()} />
        ) : (
          <>
            <div className={s.attention}>
              {ATTENTION_KINDS.map((kind) => {
                const n = attention.data.counts[kind] ?? 0;
                return (
                  <button
                    key={kind}
                    type="button"
                    className={`${s.tile} ${n === 0 ? s.tileZero : ""} ${focus === kind ? s.tileActive : ""}`}
                    style={{ "--tile-c": attentionColour(kind, n) } as CSSProperties}
                    onClick={() => set("focus", focus === kind ? null : kind)}
                    aria-pressed={focus === kind}
                    data-testid={`attention-${kind}`}
                  >
                    <span className={s.tileCount}>{n}</span>
                    <span className={s.tileName}>{t(ATTENTION_LABEL[kind])}</span>
                    <span className={s.tileWhy}>{t(ATTENTION_MEANING[kind])}</span>
                  </button>
                );
              })}
            </div>
            {focus && <AttentionList kind={focus} rows={attention.data.lists[focus]} />}
          </>
        )}
      </Card>

      <Card
        title="Portfolio"
        sub="Stopped first, then late, then the rest"
      >
        <div className={s.filters} style={{ marginBottom: 10 }}>
          <span className={s.count}>{projects.data ? tn(rows.length, "{n} project", "{n} projects") : ""}</span>
        </div>
        {projects.isPending ? (
          <Skeleton rows={6} />
        ) : projects.isError ? (
          <ErrorState error={projects.error} retry={() => void projects.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState>
            {t(
              "No projects match at {site}. Clear the filters to see them all.",
              { site: site.code },
            )}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Project")}</th>
                <th>{t("Status")}</th>
                <th>{t("Schedule")}</th>
                <th>{t("Gates")}</th>
                <th>{t("Holding")}</th>
                <th>{t("End")}</th>
                <th style={{ textAlign: "end" }}>{t("Budget")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <ProjectRowView key={p.id} p={p} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Page>
  );
}

function ProjectRowView({ p }: { p: ProjectRow }) {
  return (
    <tr className={p.workStopped ? s.stopped : undefined} data-testid={`project-${p.code}`}>
      <td>
        <Link to={`/capital-projects/${p.id}`}>{p.code}</Link>
        <div className="muted" style={{ fontSize: 13 }}>
          {p.name}
        </div>
      </td>
      <td>
        <span className={s.state} style={{ "--st-c": statusColour(p.status) } as CSSProperties}>
          {t(PROJECT_STATUS_LABEL[p.status])}
        </span>
      </td>
      <td>
        <ScheduleCell p={p} />
      </td>
      <td className={numClass}>
        {p.gates === 0 ? (
          /* Not "0 of 0 passed": a project with no gates has passed none. */
          <span
            className="muted"
            title={t("This project has no gates at all, which is not the same as having passed them.")}
          >
            {t("none")}
          </span>
        ) : (
          `${p.gatesApproved}/${p.gates}`
        )}
      </td>
      <td className={numClass}>
        {p.holding > 0 ? (
          <span className={`${s.state} ${s.stateSolid}`} style={{ "--st-c": "var(--red)" } as CSSProperties}>
            {p.holding}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>{formatDate(p.schedule.endDate)}</td>
      <td className={numClass}>
        {p.money.state === "declared" ? (
          formatMoney(p.money.budgetMinor, p.money.currency)
        ) : (
          <span className="muted" title={t("No budget has been declared for this project.")}>
            {t("NOT DECLARED")}
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * The schedule as the platform states it. The three states with no answer —
 * no baseline, no current date, no dates — are shown as those words, never as
 * a slip of zero: a project with no baseline is not on time, the question
 * simply has no answer, and putting a 0 beside genuinely on-time projects is
 * how a late project disappears.
 */
export function ScheduleCell({ p }: { p: Pick<ProjectRow, "schedule"> }) {
  const sc = p.schedule;
  const overdue = sc.overdueDays != null && sc.overdueDays > 0;
  return (
    <span title={sc.why}>
      <span className={s.state} style={{ "--st-c": scheduleColour(sc.state) } as CSSProperties}>
        {t(SCHEDULE_LABEL[sc.state])}
      </span>
      {sc.endSlipDays != null && sc.endSlipDays !== 0 && (
        <span className="mono muted" style={{ marginInlineStart: 6, fontSize: 12 }}>
          {sc.endSlipDays > 0 ? `+${sc.endSlipDays}d` : `${sc.endSlipDays}d`}
        </span>
      )}
      {overdue && (
        <span className="mono" style={{ marginInlineStart: 6, fontSize: 12, color: "var(--red)" }}>
          {t("{n}d over", { n: sc.overdueDays ?? 0 })}
        </span>
      )}
    </span>
  );
}

/** The rows behind one tile. Each list has its own columns because each is a different thing. */
function AttentionList({ kind, rows }: { kind: AttentionKind; rows: AttentionRow[] }) {
  if (rows.length === 0)
    return (
      <EmptyState>
        {t("Nothing in “{list}”.", { list: t(ATTENTION_LABEL[kind]) })} {t(ATTENTION_MEANING[kind])}
      </EmptyState>
    );
  return (
    <div style={{ marginTop: 12 }} data-testid={`attention-list-${kind}`}>
      <Table>
        <thead>
          <tr>
            <th>{t("Project")}</th>
            <th>
              {kind.startsWith("gates") ? t("Gate") : kind === "sheets_left_incomplete" ? t("Inspection") : t("Detail")}
            </th>
            <th>{t("Why it is here")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.projectId}-${r.key ?? r.inspectionId ?? i}`}>
              <td>
                <Link to={`/capital-projects/${r.projectId}`}>{r.projectCode ?? r.projectId}</Link>
                <div className="muted" style={{ fontSize: 13 }}>
                  {r.projectName ?? r.name ?? ""}
                </div>
              </td>
              <td>
                {r.inspectionId && r.reference ? (
                  <Link to={`/capital-projects/inspections/${r.inspectionId}`}>{r.reference}</Link>
                ) : (
                  <span className="mono">{r.key ?? r.reference ?? "—"}</span>
                )}
                {r.title && (
                  <div className="muted" style={{ fontSize: 13 }}>
                    {r.title}
                  </div>
                )}
              </td>
              <td className={s.said}>{attentionWhy(kind, r)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

/**
 * The reason a row is on a list, taken from what the platform sent for that
 * list. Where the platform supplied its own sentence — a hold point's
 * `holding_because` — that sentence is used rather than a second one composed
 * here that could disagree with it.
 */
function attentionWhy(kind: AttentionKind, r: AttentionRow): string {
  switch (kind) {
    case "work_stopped":
      return r.holdingBecause ?? t("unreleased hold point");
    case "gates_awaiting_decision":
      return [
        r.daysWaiting != null ? tn(r.daysWaiting, "submitted {n} day ago", "submitted {n} days ago") : t("submitted"),
        r.dueDate
          ? r.overdue
            ? t("due {date} — overdue", { date: formatDate(r.dueDate) })
            : t("due {date}", { date: formatDate(r.dueDate) })
          : null,
      ]
        .filter(Boolean)
        .join(" · ");
    case "gates_decided_without_evidence":
      return t("decided with required evidence never supplied; recorded on the gate for the life of the project");
    case "sheets_left_incomplete":
      return t("recorded as '{result}' with {n} of {total} items unchecked", {
        result: r.result ?? "—",
        n: r.notChecked ?? 0,
        total: r.items ?? 0,
      });
    case "overdue":
      return t("{days} past the current end date of {date}", {
        days: tn(r.daysOverdue ?? 0, "{n} day", "{n} days"),
        date: formatDate(r.endDate),
      });
    case "slipped_against_baseline":
      return t("{days} later than the baseline of {date}", {
        days: tn(r.endSlipDays ?? 0, "{n} day", "{n} days"),
        date: formatDate(r.baselineEnd),
      });
    case "no_baseline":
      return t("live with no baseline, so nothing here can say whether it is late");
    case "never_inspected":
      return t("{status} with no inspection recorded at all", {
        status: r.status ? t(PROJECT_STATUS_LABEL[r.status]) : t("live"),
      });
    case "rebaselined":
      return tn(
        r.rebaselined ?? 1,
        "the baseline has been moved {n} time; slip is measured against the current one",
        "the baseline has been moved {n} times; slip is measured against the current one",
      );
  }
}

function attentionColour(kind: AttentionKind, n: number): string {
  if (n === 0) return "var(--idle)";
  if (kind === "work_stopped" || kind === "gates_decided_without_evidence" || kind === "overdue") return "var(--red)";
  if (kind === "no_baseline" || kind === "never_inspected") return "var(--dim)";
  return "var(--amber)";
}

function statusColour(status: ProjectStatus): string {
  if (status === "cancelled") return "var(--dim)";
  if (status === "complete") return "var(--green)";
  if (status === "on_hold") return "var(--amber)";
  if (status === "construction" || status === "commissioning") return "var(--blue)";
  return "var(--muted)";
}

function scheduleColour(state: ProjectRow["schedule"]["state"]): string {
  if (state === "slipped") return "var(--amber)";
  if (state === "ahead" || state === "on_baseline") return "var(--green)";
  /* The three unanswerable states are neutral, not green. */
  return "var(--dim)";
}
